import React, { useEffect, useMemo, useState } from "react";
import { Chart } from "primereact/chart";

interface BarChartDataset {
  label?: string;
  data?: number[];
  backgroundColor?: string;
  borderColor?: string;
}

interface BarChartData {
  labels: string[];
  datasets: BarChartDataset[];
}

interface BarChartProps {
  chartData?: Partial<BarChartData> | null;
}

export default function BarChart({ chartData }: BarChartProps) {
  const [chartOptions, setChartOptions] = useState({});

  useEffect(() => {
    const documentStyle = getComputedStyle(document.documentElement);
    const textColor = documentStyle.getPropertyValue("--heading-primary");
    const textColorSecondary = documentStyle.getPropertyValue("--nav-text-secondary");

    const options = {
      maintainAspectRatio: false,
      aspectRatio: 0.8,
      plugins: {
        legend: {
          display: true,
          position: "top" as const,
          labels: {
            color: textColor,
            usePointStyle: true,
            pointStyle: "rectRounded" as const,
          },
        },
      },
      scales: {
        x: {
          ticks: {
            color: textColorSecondary,
            font: {
              weight: 500,
            },
          },
          grid: {
            display: false,
            drawBorder: true,
          },
        },
        y: {
          ticks: {
            color: textColorSecondary,
          },
          grid: {
            display: true,
            drawBorder: true,
          },
        },
      },
    };

    setChartOptions(options);
  }, []);

  const resolvedChartData = useMemo(() => {
    const labels = Array.isArray(chartData?.labels) ? chartData.labels : [];
    const datasets = Array.isArray(chartData?.datasets)
      ? chartData.datasets.map((dataset) => ({
          label: dataset.label ?? "Sales",
          data: Array.isArray(dataset.data) ? dataset.data : [],
          backgroundColor: dataset.backgroundColor ?? "#4394E5",
          borderColor: dataset.borderColor ?? "#4394E5",
        }))
      : [];

    return {
      labels,
      datasets,
    };
  }, [chartData]);

  const hasData = resolvedChartData.datasets.some((dataset) => dataset.data.length > 0);

  return (
    <div className="h-full w-full">
      {hasData ? (
        <Chart type="bar" data={resolvedChartData} options={chartOptions} />
      ) : (
        <div className="h-full w-full flex items-center justify-center">
          <p className="text-sm apollo-body" style={{ color: "var(--nav-text-secondary)" }}>
            No revenue data available.
          </p>
        </div>
      )}
    </div>
  );
}
